import { BOOKING_ADD_ONS } from '@/components/booking/shared/addOnsCatalog';
import { CONTRACT_DISCOUNTS } from '@/lib/pricing/discount-policy';

/** Service types available in booking flow (matches ServiceType enum) */
export const BOOKING_SERVICE_TYPES = [
  'home_cleaning',
  'office_cleaning',
  'business_cleaning',
  'stairwell_cleaning',
  'move_in_out',
  'window_cleaning',
  'post_construction',
  'hoarding_cleaning',
  'furniture_transport',
] as const;

export type BookingServiceType = (typeof BOOKING_SERVICE_TYPES)[number];

export interface PricingConfig {
  /** € per extra room beyond first (used in price formula) */
  room_charge?: number;
  /** € per extra bathroom beyond first (used in price formula) */
  bathroom_charge?: number;
  size_tiers: Record<
    string,
    {
      basePrice: number;
      min?: number;
      max?: number;
      name?: string;
    }
  >;
  effort_multipliers: Record<string, number>;
  business_size_tiers: Record<
    string,
    { basePrice: number; min?: number; max?: number; name?: string }
  >;
  frequency_multipliers: Record<string, number>;
  priority_multipliers: Record<string, number>;
  contract_discounts: Record<string, number>;
  floor_type_adjustments: Record<string, number>;
  add_ons_pricing: Record<string, number>;
  /** Enabled service types in booking flow. When absent, all are enabled. */
  booking_services?: string[];
  /** Service-specific pricing (Office, Move-in/out, Construction, Windows, Hoarding, Stairwell) */
  service_pricing?: ServicePricing;
}

export interface ServicePricing {
  office_price_per_sqm: number;
  office_bathroom_price: number;
  move_in_price_per_sqm: number;
  move_out_price_per_sqm: number;
  move_both_price_per_sqm: number;
  construction_price_per_sqm: number;
  construction_window_price: number;
  construction_standard_mult: number;
  construction_heavy_mult: number;
  window_price_per_window: number;
  hoarding_light_rate: number;
  hoarding_standard_rate: number;
  hoarding_heavy_rate: number;
  hoarding_room_charge: number;
  hoarding_bathroom_charge: number;
  stairwell_base: number;
  stairwell_per_floor: number;
  stairwell_handrails_per_floor: number;
  stairwell_mailboxes: number;
  stairwell_doors: number;
  stairwell_basement: number;
}

export const FALLBACK_SERVICE_PRICING: ServicePricing = {
  office_price_per_sqm: 0.6895,
  office_bathroom_price: 4.2,
  move_in_price_per_sqm: 3.2,
  move_out_price_per_sqm: 3.0,
  move_both_price_per_sqm: 2.8,
  construction_price_per_sqm: 2,
  construction_window_price: 6,
  construction_standard_mult: 2.9 / 2,
  construction_heavy_mult: 3.8 / 2,
  window_price_per_window: 6,
  hoarding_light_rate: 6,
  hoarding_standard_rate: 8,
  hoarding_heavy_rate: 10,
  hoarding_room_charge: 10,
  hoarding_bathroom_charge: 12,
  stairwell_base: 15,
  stairwell_per_floor: 4,
  stairwell_handrails_per_floor: 2,
  stairwell_mailboxes: 1.5,
  stairwell_doors: 3,
  stairwell_basement: 10,
};

/** Residential size tiers aligned with public pricing page (25-40 … 276-350 m²) — Bulgaria market pricing (pre-VAT) */
export const FALLBACK_PRICING_CONFIG: PricingConfig = {
  room_charge: 5.5,
  bathroom_charge: 5,
  size_tiers: {
    tier_1: { basePrice: 36, min: 25, max: 40, name: 'Compact' },
    tier_2: { basePrice: 47.45, min: 45, max: 60, name: 'Small' },
    tier_3: { basePrice: 54, min: 65, max: 80, name: 'Standard' },
    tier_4: { basePrice: 60.55, min: 85, max: 100, name: 'Large' },
    tier_5: { basePrice: 76.91, min: 105, max: 150, name: 'XL' },
    tier_6: { basePrice: 83.95, min: 150, max: 200, name: 'XXL' },
    tier_7: { basePrice: 106.04, min: 201, max: 275, name: 'Estate' },
    tier_8: { basePrice: 128.13, min: 276, max: 350, name: 'Grand Estate' },
  },
  /** Residential effort levels (Regular & Expert) - used by booking form and pricing page */
  effort_multipliers: {
    regular: 1.0,
    expert: 1.5,
  },
  business_size_tiers: {
    small: { basePrice: 48, min: 50, max: 150, name: 'Small Business' },
    medium: { basePrice: 72, min: 150, max: 300, name: 'Medium Business' },
    large: { basePrice: 108, min: 300, max: 500, name: 'Large Business' },
    xlarge: { basePrice: 150, min: 500, max: 1000, name: 'Extra Large Business' },
  },
  frequency_multipliers: {
    daily: 0.7,
    weekly: 1.0,
    monthly: 1.3,
  },
  priority_multipliers: {
    quality: 1.15,
    reliability: 1.1,
    price: 1.0,
  },
  contract_discounts: {
    ...CONTRACT_DISCOUNTS,
  },
  floor_type_adjustments: {
    vinyl: 1.0,
    laminate: 1.0,
    wooden: 1.1,
    wood_laminate: 1.05,
    tiles: 1.0,
    carpet: 1.2,
    concrete: 0.9,
    other: 1.0,
  },
  add_ons_pricing: Object.fromEntries(BOOKING_ADD_ONS.map(addOn => [addOn.id, addOn.price])),
  booking_services: [...BOOKING_SERVICE_TYPES],
  service_pricing: FALLBACK_SERVICE_PRICING,
};

export const EFFORT_ORDER = ['regular', 'expert'] as const;
export const FREQUENCY_ORDER = Object.keys(FALLBACK_PRICING_CONFIG.frequency_multipliers);
export const PRIORITY_ORDER = Object.keys(FALLBACK_PRICING_CONFIG.priority_multipliers);
export const CONTRACT_ORDER = Object.keys(FALLBACK_PRICING_CONFIG.contract_discounts);
export const FLOOR_ORDER = Object.keys(FALLBACK_PRICING_CONFIG.floor_type_adjustments);

export function clonePricingConfig(config: Partial<PricingConfig>): PricingConfig {
  if (typeof structuredClone === 'function') {
    return structuredClone({
      ...FALLBACK_PRICING_CONFIG,
      ...config,
      room_charge: config?.room_charge ?? FALLBACK_PRICING_CONFIG.room_charge ?? 5.5,
      bathroom_charge: config?.bathroom_charge ?? FALLBACK_PRICING_CONFIG.bathroom_charge ?? 5,
      size_tiers: {
        ...FALLBACK_PRICING_CONFIG.size_tiers,
        ...(config.size_tiers || {}),
      },
      effort_multipliers: {
        ...FALLBACK_PRICING_CONFIG.effort_multipliers,
        ...(config.effort_multipliers || {}),
      },
      business_size_tiers: {
        ...FALLBACK_PRICING_CONFIG.business_size_tiers,
        ...(config.business_size_tiers || {}),
      },
      frequency_multipliers: {
        ...FALLBACK_PRICING_CONFIG.frequency_multipliers,
        ...(config.frequency_multipliers || {}),
      },
      priority_multipliers: {
        ...FALLBACK_PRICING_CONFIG.priority_multipliers,
        ...(config.priority_multipliers || {}),
      },
      contract_discounts: {
        ...FALLBACK_PRICING_CONFIG.contract_discounts,
        ...(config.contract_discounts || {}),
      },
      floor_type_adjustments: {
        ...FALLBACK_PRICING_CONFIG.floor_type_adjustments,
        ...(config.floor_type_adjustments || {}),
      },
      add_ons_pricing: {
        ...FALLBACK_PRICING_CONFIG.add_ons_pricing,
        ...(config.add_ons_pricing || {}),
      },
      service_pricing: {
        ...FALLBACK_SERVICE_PRICING,
        ...(config.service_pricing || {}),
      },
    }) as PricingConfig;
  }
  return JSON.parse(
    JSON.stringify({
      ...FALLBACK_PRICING_CONFIG,
      ...config,
      size_tiers: {
        ...FALLBACK_PRICING_CONFIG.size_tiers,
        ...(config.size_tiers || {}),
      },
      effort_multipliers: {
        ...FALLBACK_PRICING_CONFIG.effort_multipliers,
        ...(config.effort_multipliers || {}),
      },
      business_size_tiers: {
        ...FALLBACK_PRICING_CONFIG.business_size_tiers,
        ...(config.business_size_tiers || {}),
      },
      frequency_multipliers: {
        ...FALLBACK_PRICING_CONFIG.frequency_multipliers,
        ...(config.frequency_multipliers || {}),
      },
      priority_multipliers: {
        ...FALLBACK_PRICING_CONFIG.priority_multipliers,
        ...(config.priority_multipliers || {}),
      },
      contract_discounts: {
        ...FALLBACK_PRICING_CONFIG.contract_discounts,
        ...(config.contract_discounts || {}),
      },
      floor_type_adjustments: {
        ...FALLBACK_PRICING_CONFIG.floor_type_adjustments,
        ...(config.floor_type_adjustments || {}),
      },
      add_ons_pricing: {
        ...FALLBACK_PRICING_CONFIG.add_ons_pricing,
        ...(config.add_ons_pricing || {}),
      },
      service_pricing: {
        ...FALLBACK_SERVICE_PRICING,
        ...(config.service_pricing || {}),
      },
    }),
  ) as PricingConfig;
}

export const formatPricingLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

/** Normalize/migrate config to ensure correct 8-tier size + 4-tier business structure. */
export function normalizePricingConfig(
  config: PricingConfig | Partial<PricingConfig> | null,
): PricingConfig {
  if (!config) {
    return clonePricingConfig(FALLBACK_PRICING_CONFIG);
  }

  const normalized = clonePricingConfig(config);

  const normalizeNumber = (value: unknown, fallback: number): number => {
    if (value === null || value === undefined) {
      return fallback;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const replaceLegacyNumber = (value: number, legacy: number, next: number): number => {
    return Math.abs(value - legacy) < 1e-6 ? next : value;
  };

  const normalizeOptionalNumber = (
    value: unknown,
    fallback: number | undefined,
  ): number | undefined => {
    if (value === null || value === undefined) {
      return fallback;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const normalizeKeys = <T>(source?: Record<string, T>) => {
    const result: Record<string, T> = {};
    Object.entries(source ?? {}).forEach(([key, value]) => {
      result[key.toLowerCase()] = value;
    });
    return result;
  };

  const resolveByKey = <T>(source: Record<string, T>, keys: string[]) => {
    for (const key of keys) {
      if (source[key] !== undefined) {
        return source[key];
      }
    }
    return undefined;
  };

  const sizeTierLookup = normalizeKeys(normalized.size_tiers);

  const buildTierEntry = (key: keyof typeof FALLBACK_PRICING_CONFIG.size_tiers) => {
    const fallbackTier = FALLBACK_PRICING_CONFIG.size_tiers[key];
    const sourceTier = sizeTierLookup[key] as
      { basePrice?: number; min?: number; max?: number; name?: string } | undefined;
    const hasSource = !!sourceTier;
    return {
      basePrice: normalizeNumber(sourceTier?.basePrice, fallbackTier.basePrice),
      min: hasSource
        ? normalizeOptionalNumber(sourceTier?.min, fallbackTier.min)
        : (fallbackTier.min ?? undefined),
      max: hasSource
        ? normalizeOptionalNumber(sourceTier?.max, fallbackTier.max)
        : (fallbackTier.max ?? undefined),
      name: fallbackTier.name,
    };
  };

  normalized.size_tiers = {
    tier_1: buildTierEntry('tier_1'),
    tier_2: buildTierEntry('tier_2'),
    tier_3: buildTierEntry('tier_3'),
    tier_4: buildTierEntry('tier_4'),
    tier_5: buildTierEntry('tier_5'),
    tier_6: buildTierEntry('tier_6'),
    tier_7: buildTierEntry('tier_7'),
    tier_8: buildTierEntry('tier_8'),
  };

  normalized.room_charge = normalizeNumber(
    config.room_charge ?? (config as Record<string, unknown>).room_charge,
    FALLBACK_PRICING_CONFIG.room_charge ?? 5.5,
  );
  normalized.bathroom_charge = normalizeNumber(
    config.bathroom_charge ?? (config as Record<string, unknown>).bathroom_charge,
    FALLBACK_PRICING_CONFIG.bathroom_charge ?? 5,
  );

  const effortLookup = normalizeKeys<number>(normalized.effort_multipliers);
  normalized.effort_multipliers = {
    regular: normalizeNumber(
      resolveByKey(effortLookup, ['regular', 'basic']),
      FALLBACK_PRICING_CONFIG.effort_multipliers.regular,
    ),
    expert: normalizeNumber(
      resolveByKey(effortLookup, ['expert', 'standard']),
      FALLBACK_PRICING_CONFIG.effort_multipliers.expert,
    ),
  };

  const businessLookup = normalizeKeys(normalized.business_size_tiers);
  const smallBusiness = resolveByKey(businessLookup, ['small', 'small_business', 'smallbusiness']);
  const mediumBusiness = resolveByKey(businessLookup, [
    'medium',
    'medium_business',
    'mediumbusiness',
  ]);
  const largeBusiness = resolveByKey(businessLookup, ['large', 'large_business', 'largebusiness']);
  const xlargeBusiness = resolveByKey(businessLookup, [
    'xlarge',
    'extra_large',
    'extra-large',
    'xl',
  ]);

  normalized.business_size_tiers = {
    small: {
      ...FALLBACK_PRICING_CONFIG.business_size_tiers.small,
      ...(smallBusiness || {}),
      basePrice: normalizeNumber(
        smallBusiness?.basePrice,
        FALLBACK_PRICING_CONFIG.business_size_tiers.small.basePrice,
      ),
      min: normalizeOptionalNumber(
        smallBusiness?.min,
        FALLBACK_PRICING_CONFIG.business_size_tiers.small.min,
      ),
      max: normalizeOptionalNumber(
        smallBusiness?.max,
        FALLBACK_PRICING_CONFIG.business_size_tiers.small.max,
      ),
      name: smallBusiness?.name || FALLBACK_PRICING_CONFIG.business_size_tiers.small.name,
    },
    medium: {
      ...FALLBACK_PRICING_CONFIG.business_size_tiers.medium,
      ...(mediumBusiness || {}),
      basePrice: normalizeNumber(
        mediumBusiness?.basePrice,
        FALLBACK_PRICING_CONFIG.business_size_tiers.medium.basePrice,
      ),
      min: normalizeOptionalNumber(
        mediumBusiness?.min,
        FALLBACK_PRICING_CONFIG.business_size_tiers.medium.min,
      ),
      max: normalizeOptionalNumber(
        mediumBusiness?.max,
        FALLBACK_PRICING_CONFIG.business_size_tiers.medium.max,
      ),
      name: mediumBusiness?.name || FALLBACK_PRICING_CONFIG.business_size_tiers.medium.name,
    },
    large: {
      ...FALLBACK_PRICING_CONFIG.business_size_tiers.large,
      ...(largeBusiness || {}),
      basePrice: normalizeNumber(
        largeBusiness?.basePrice,
        FALLBACK_PRICING_CONFIG.business_size_tiers.large.basePrice,
      ),
      min: normalizeOptionalNumber(
        largeBusiness?.min,
        FALLBACK_PRICING_CONFIG.business_size_tiers.large.min ?? undefined,
      ),
      max: normalizeOptionalNumber(
        largeBusiness?.max,
        FALLBACK_PRICING_CONFIG.business_size_tiers.large.max ?? undefined,
      ),
      name: largeBusiness?.name || FALLBACK_PRICING_CONFIG.business_size_tiers.large.name,
    },
    xlarge: {
      ...FALLBACK_PRICING_CONFIG.business_size_tiers.xlarge,
      ...(xlargeBusiness || {}),
      basePrice: normalizeNumber(
        xlargeBusiness?.basePrice,
        FALLBACK_PRICING_CONFIG.business_size_tiers.xlarge.basePrice,
      ),
      min: normalizeOptionalNumber(
        xlargeBusiness?.min,
        FALLBACK_PRICING_CONFIG.business_size_tiers.xlarge.min,
      ),
      max: normalizeOptionalNumber(
        xlargeBusiness?.max,
        FALLBACK_PRICING_CONFIG.business_size_tiers.xlarge.max ?? undefined,
      ),
      name: xlargeBusiness?.name || FALLBACK_PRICING_CONFIG.business_size_tiers.xlarge.name,
    },
  };

  const normalizeNumericRecord = (
    source: Record<string, number> | undefined,
    fallback: Record<string, number>,
  ) => {
    const output: Record<string, number> = { ...fallback };
    Object.entries(source ?? {}).forEach(([key, value]) => {
      output[key] = normalizeNumber(value, fallback[key] ?? 0);
    });
    return output;
  };

  normalized.frequency_multipliers = normalizeNumericRecord(
    normalized.frequency_multipliers,
    FALLBACK_PRICING_CONFIG.frequency_multipliers,
  );
  normalized.priority_multipliers = normalizeNumericRecord(
    normalized.priority_multipliers,
    FALLBACK_PRICING_CONFIG.priority_multipliers,
  );
  normalized.contract_discounts = normalizeNumericRecord(
    normalized.contract_discounts,
    FALLBACK_PRICING_CONFIG.contract_discounts,
  );
  normalized.floor_type_adjustments = normalizeNumericRecord(
    normalized.floor_type_adjustments,
    FALLBACK_PRICING_CONFIG.floor_type_adjustments,
  );

  normalized.add_ons_pricing = normalized.add_ons_pricing ?? {};
  Object.entries(normalized.add_ons_pricing).forEach(([key, value]) => {
    normalized.add_ons_pricing[key] = normalizeNumber(
      value,
      FALLBACK_PRICING_CONFIG.add_ons_pricing[key] ?? 0,
    );
  });

  BOOKING_ADD_ONS.forEach(addOn => {
    if (!(addOn.id in normalized.add_ons_pricing)) {
      normalized.add_ons_pricing[addOn.id] = addOn.price;
    }
  });

  const rawBookingServices = normalized.booking_services;
  normalized.booking_services = Array.isArray(rawBookingServices)
    ? rawBookingServices.filter(
        (s): s is string =>
          typeof s === 'string' && BOOKING_SERVICE_TYPES.includes(s as BookingServiceType),
      )
    : [...BOOKING_SERVICE_TYPES];

  const sp = (config.service_pricing ?? (config as Record<string, unknown>).service_pricing) as
    Partial<ServicePricing> | null | undefined;
  normalized.service_pricing = {
    office_price_per_sqm: normalizeNumber(
      sp?.office_price_per_sqm,
      FALLBACK_SERVICE_PRICING.office_price_per_sqm,
    ),
    office_bathroom_price: normalizeNumber(
      sp?.office_bathroom_price,
      FALLBACK_SERVICE_PRICING.office_bathroom_price,
    ),
    move_in_price_per_sqm: normalizeNumber(
      sp?.move_in_price_per_sqm,
      FALLBACK_SERVICE_PRICING.move_in_price_per_sqm,
    ),
    move_out_price_per_sqm: normalizeNumber(
      sp?.move_out_price_per_sqm,
      FALLBACK_SERVICE_PRICING.move_out_price_per_sqm,
    ),
    move_both_price_per_sqm: normalizeNumber(
      sp?.move_both_price_per_sqm,
      FALLBACK_SERVICE_PRICING.move_both_price_per_sqm,
    ),
    construction_price_per_sqm: replaceLegacyNumber(
      normalizeNumber(
        sp?.construction_price_per_sqm,
        FALLBACK_SERVICE_PRICING.construction_price_per_sqm,
      ),
      3.2,
      FALLBACK_SERVICE_PRICING.construction_price_per_sqm,
    ),
    construction_window_price: normalizeNumber(
      sp?.construction_window_price,
      FALLBACK_SERVICE_PRICING.construction_window_price,
    ),
    construction_standard_mult: replaceLegacyNumber(
      normalizeNumber(
        sp?.construction_standard_mult,
        FALLBACK_SERVICE_PRICING.construction_standard_mult,
      ),
      4.6 / 3.2,
      FALLBACK_SERVICE_PRICING.construction_standard_mult,
    ),
    construction_heavy_mult: replaceLegacyNumber(
      normalizeNumber(
        sp?.construction_heavy_mult,
        FALLBACK_SERVICE_PRICING.construction_heavy_mult,
      ),
      6.8 / 3.2,
      FALLBACK_SERVICE_PRICING.construction_heavy_mult,
    ),
    window_price_per_window: normalizeNumber(
      sp?.window_price_per_window,
      FALLBACK_SERVICE_PRICING.window_price_per_window,
    ),
    hoarding_light_rate: normalizeNumber(
      sp?.hoarding_light_rate,
      FALLBACK_SERVICE_PRICING.hoarding_light_rate,
    ),
    hoarding_standard_rate: normalizeNumber(
      sp?.hoarding_standard_rate,
      FALLBACK_SERVICE_PRICING.hoarding_standard_rate,
    ),
    hoarding_heavy_rate: normalizeNumber(
      sp?.hoarding_heavy_rate,
      FALLBACK_SERVICE_PRICING.hoarding_heavy_rate,
    ),
    hoarding_room_charge: normalizeNumber(
      sp?.hoarding_room_charge,
      FALLBACK_SERVICE_PRICING.hoarding_room_charge,
    ),
    hoarding_bathroom_charge: normalizeNumber(
      sp?.hoarding_bathroom_charge,
      FALLBACK_SERVICE_PRICING.hoarding_bathroom_charge,
    ),
    stairwell_base: normalizeNumber(sp?.stairwell_base, FALLBACK_SERVICE_PRICING.stairwell_base),
    stairwell_per_floor: normalizeNumber(
      sp?.stairwell_per_floor,
      FALLBACK_SERVICE_PRICING.stairwell_per_floor,
    ),
    stairwell_handrails_per_floor: normalizeNumber(
      sp?.stairwell_handrails_per_floor,
      FALLBACK_SERVICE_PRICING.stairwell_handrails_per_floor,
    ),
    stairwell_mailboxes: normalizeNumber(
      sp?.stairwell_mailboxes,
      FALLBACK_SERVICE_PRICING.stairwell_mailboxes,
    ),
    stairwell_doors: normalizeNumber(sp?.stairwell_doors, FALLBACK_SERVICE_PRICING.stairwell_doors),
    stairwell_basement: normalizeNumber(
      sp?.stairwell_basement,
      FALLBACK_SERVICE_PRICING.stairwell_basement,
    ),
  };

  return normalized;
}
